/**
 * The local-dev tenant, made explicit (tenancy audit item 5, 4 Nov 2026).
 *
 * On plain `localhost` / `127.0.0.1` the tenant used to default silently to `goodstuff365`. Now it
 * comes from, in order:
 *   1. localStorage `dev_tenant_override` (set by hand in devtools);
 *   2. `VITE_DEV_DEFAULT_TENANT` from frontend/.env.local — only in a Vite dev server
 *      (`import.meta.env.DEV`), with a loud console warning;
 *   3. nothing: the app shows "no tenant selected" with the ways to pick one.
 * `http://<tenant>.localhost:3000` needs none of this (TenantContext reads the host).
 * A production build has import.meta.env.DEV === false, so step 2 never applies there.
 */

const SUBDOMAIN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
let warned: string | null = null;

/** VITE_DEV_DEFAULT_TENANT, only in the Vite dev server; null otherwise (always null in production). */
export function devDefaultTenant(): string | null {
  if (!import.meta.env.DEV) { return null; }
  const raw = String(import.meta.env.VITE_DEV_DEFAULT_TENANT ?? '').trim().toLowerCase();
  if (!raw || !SUBDOMAIN.test(raw)) { return null; }
  if (warned !== raw) {
    warned = raw;
    console.warn(`[tenant] ⚠ VITE_DEV_DEFAULT_TENANT is set: plain localhost uses tenant "${raw}". Local dev only.`);
  }
  return raw;
}

/** The tenant for plain localhost / 127.0.0.1: explicit override, else explicit dev default, else none. */
export function localDevTenant(): string | null {
  let override: string | null = null;
  try { override = localStorage.getItem('dev_tenant_override'); } catch { override = null; }
  return (override && override.trim()) || devDefaultTenant();
}

/** Plain localhost or 127.0.0.1 (not <tenant>.localhost). */
export function isPlainLocalhost(hostname: string): boolean {
  return hostname === 'localhost' || hostname.startsWith('127.0.0.1');
}

/** Shown when plain localhost has no tenant. */
export const LOCAL_NO_TENANT_MESSAGE =
  'Local dev has no tenant selected. Open http://<tenant>.localhost:3000, or set VITE_DEV_DEFAULT_TENANT in frontend/.env.local, '
  + "or run localStorage.setItem('dev_tenant_override', '<tenant>') in the console.";
